import { useCallback, useEffect, useState } from 'react';

import { createStorage } from '@sg/mmkv-storage';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Controls whether to show the featured reward hero for the current customer.
 *
 * The hook compares the current customer and reward combination with the last
 * combination stored in MMKV. A new combination is shown and recorded as seen,
 * while the previously recorded combination stays hidden. It also hides stale
 * content and reports loading while a changed combination is synchronized.
 */
export const useLoyaltyFeaturedReward = (params: Params) => {
  const { customerId, rewardId } = params;

  // ─── State ─────────────────────────────────────────────────────────────────

  const [state, setState] = useState(() =>
    getFeaturedRewardState(getStorageValue({ customerId, rewardId })),
  );

  // ─── Derived ───────────────────────────────────────────────────────────────

  const storageValue = getStorageValue({ customerId, rewardId });
  const isLoading = Boolean(storageValue) && state.storageValue !== storageValue;
  const isVisible = state.storageValue === storageValue && state.isVisible;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const hide = useCallback(() => {
    setState((currentState) => ({ ...currentState, isVisible: false }));
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (state.storageValue !== storageValue) {
      setState(getFeaturedRewardState(storageValue));
      return;
    }

    if (!state.isVisible || !storageValue) return;

    STORAGE.set(FEATURED_REWARD_SEEN_KEY, storageValue);
  }, [state.isVisible, state.storageValue, storageValue]);

  // ───────────────────────────────────────────────────────────────────────────

  return { hide, isLoading, isVisible };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStorageValue(params: Params) {
  const { customerId, rewardId } = params;

  if (!customerId || !rewardId) return undefined;

  return `${customerId}_${rewardId}`;
}

function getFeaturedRewardState(storageValue: string | undefined): State {
  if (!storageValue) return { isVisible: false, storageValue: undefined };

  const isVisible = STORAGE.getString(FEATURED_REWARD_SEEN_KEY) !== storageValue;

  return { isVisible, storageValue };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE = createStorage({ id: 'sg.loyalty' });
const FEATURED_REWARD_SEEN_KEY = 'loyalty-featured-reward-seen';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  customerId?: string;
  rewardId?: string;
};

type State = {
  isVisible: boolean;
  storageValue?: string;
};
